import { memo } from "react";
import { beforeOf, partColumn, partIssue, rowHasIssue, statusTitle } from "./bomView";

// Marks the searched text inside a value
const Highlight = ({ text, query }) => {
  if (!query || !text) return text || "";
  const at = text.toLowerCase().indexOf(query);
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <mark className="bom-table__mark">{text.slice(at, at + query.length)}</mark>
      {text.slice(at + query.length)}
    </>
  );
};

const Chevron = ({ open }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" className={open ? "bom-table__chevron is-open" : "bom-table__chevron"}>
    <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Header tooltip of a column that only one file of a comparison has
const onlyIn = (column, sides) =>
  sides && column.onlyIn ? `Only in ${sides[column.onlyIn].shortLabel}, not compared` : undefined;

// One table for tree and list views. items come from visibleRows(). In the comparison viewer rowClassName colors
// the rows by status and sides ({ a, b }) adds a status column and shows both values of the changed cells.
function BomTable({ section, columns, items, treeCol, isTree, query, onToggle, selectedId, onSelect, rowClassName, sides }) {
  const q = (query || "").trim().toLowerCase();
  const quantity = section.columns.findIndex((c) => c.role === "quantity");
  // NOT FOUND and ERROR part numbers are marked in every view: that BOM must never be released
  const part = partColumn(section);

  return (
    <table className="table table-sm table-hover bom-table mb-0">
      <thead>
        <tr>
          {sides && (
            <th scope="col" className="bom-table__status">
              <span className="visually-hidden">Status</span>
            </th>
          )}
          {columns.map((i) => (
            <th key={i} scope="col" className={i === quantity ? "text-end" : undefined} title={onlyIn(section.columns[i], sides)}>
              {section.columns[i].label}
              {sides && section.columns[i].onlyIn && <span className="bom-table__only">{sides[section.columns[i].onlyIn].shortLabel} only</span>}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map(({ row, hasChildren, expanded, context }) => (
          <tr
            key={row.id}
            className={[
              row.id === selectedId && "is-selected",
              context && "is-context",
              rowClassName && rowClassName(row),
              rowHasIssue(section, row, part) && "has-issue",
            ].filter(Boolean).join(" ")}
            onClick={() => onSelect(row)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSelect(row);
              if (hasChildren && e.key === "ArrowRight" && !expanded) onToggle(row.id);
              if (hasChildren && e.key === "ArrowLeft" && expanded) onToggle(row.id);
            }}
            tabIndex={0}
            aria-selected={row.id === selectedId}
          >
            {sides && (
              <td className="bom-table__status">
                {row.status && row.status !== "same" && (
                  <span className={`bom-status bom-status--${row.status}`} title={statusTitle(section, row, sides)}>
                    <span className="visually-hidden">{statusTitle(section, row, sides)}</span>
                  </span>
                )}
              </td>
            )}
            {columns.map((i) => {
              const value = row.values[i];
              const before = sides ? beforeOf(row, i) : undefined;
              const changed = before !== undefined;
              const shown = i === part && partIssue(value) ? <span className="bom-issue">{value}</span> : null;
              const text = changed ? (
                <span title={`${sides.b.shortLabel}: ${before || "(empty)"} → ${sides.a.shortLabel}: ${value || "(empty)"}`}>
                  <span className="bom-diff__before">{before || "(empty)"}</span>{" "}
                  {shown || (value ? <Highlight text={value} query={q} /> : <span className="bom-diff__empty">(empty)</span>)}
                </span>
              ) : (
                shown || <Highlight text={value} query={q} />
              );
              if (isTree && i === treeCol) {
                return (
                  <td key={i} className={`bom-table__tree${changed ? " is-changed" : ""}`} style={{ paddingLeft: `${0.35 + row.depth * 1.1}rem` }}>
                    {hasChildren ? (
                      <button
                        type="button"
                        className="bom-table__toggle"
                        aria-expanded={expanded}
                        aria-label={`${expanded ? "Collapse" : "Expand"} ${value || "row"}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggle(row.id);
                        }}
                      >
                        <Chevron open={expanded} />
                      </button>
                    ) : (
                      <span className="bom-table__leaf" aria-hidden="true" />
                    )}
                    {text}
                  </td>
                );
              }
              const className = [i === quantity && "text-end", changed && "is-changed"].filter(Boolean).join(" ");
              return (
                <td key={i} className={className || undefined}>
                  {text}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default memo(BomTable);
